/**
 * THE THINGS THAT RUN WITHOUT YOU.
 *
 * Anthony: "only 2 of 19 automations have a switch... yes fix, and get
 * this in order please."
 *
 * Every automation was already a row in `automations` with an `enabled`
 * flag the guard reads before anything is sent. Two of them had a switch,
 * on the Text Desk page. The other eighteen could only be changed by
 * editing the database -- which is why "Stop the claims SMS campaign until
 * I get back" had to be a text message to me rather than a tap.
 *
 * ORDERED BY WHO IS WAITING. The front door first, because those are
 * strangers being answered or not answered; then the money; then the crew,
 * who can ask a person when something stops. Alphabetical would have been
 * easier and would put "car catcher" above "lead textback", which is not
 * the order anybody thinks in at eight in the morning.
 *
 * SWITCHING ON SETS THE FLOOR TO NOW. Every sender is floored so that
 * turning one back on cannot sweep up a week of missed calls and text all
 * of them at once. That is the most expensive mistake this screen could
 * make, so it is not offered as a choice.
 */

import { db } from "../data/client.ts";
import { esc, ago, mount, since } from "../ui/dom.ts";
import { shopDayStartIso } from "../ui/shopclock.ts";
import type { Role } from "../domain/permissions.ts";

const BOSS: Role[] = ["admin", "supervisor"];

type Row = {
  key: string; enabled: boolean; daily_cap: number | null;
  window_start: number | null; window_end: number | null;
  floor: string | null; note: string | null;
  /** Only forward_to is read here. It is the difference between a
   *  rehearsal and a stranger being answered. */
  settings: Record<string, unknown> | null;
};

/** What each one does, in the words a person would use. */
const SAYS: Record<string, string> = {
  lead_textback: "Texts back a missed call before they ring the next shop.",
  autofix_line: "Answers a stranger's car question texted to 413-AUTOFIX. Two free, then a $5 day pass.",
  textback_nudge: "One follow-up five minutes after an unanswered opener. Never a third.",
  intake_agent: "Gathers what a quote needs, one question at a time. Never quotes, never books.",
  callrail_intake: "Puts every call on the board within two minutes, in market or out.",
  callrail_texts: "Texts sent to the tracking numbers, which nothing else was reading.",
  email_lead: "Reads the published inboxes and answers a real enquiry.",
  ghl_forms: "Forms filled in on supercanic.com, straight onto the board.",
  car_catcher: "Lifts the year, make and model out of a reply. Sends nothing.",

  quote_drafter: "Drafts a price from the conversation. A person still sends it.",
  quote_chase: "One ask a day on a quote nobody answered, four at most.",
  stale_quotes: "Closes a quote nobody answered in a month. Reopen it if they ring.",
  ppi_quote_send: "The one quote that sends itself, because that price is published.",
  ppi_quote_drip: "Follows up a pre-purchase quote in the buyer's own clock: two hours later, the next morning, two days after that. Stops on any answer. While it is on, the daily chase leaves these quotes alone.",
  ppi_sms_brand: "Signs pre-purchase inspection texts \"PPI Auto\" instead of the shop's name. Off until the texting registration covers that name.",
  visit_quote_send: "Offers a free estimate visit by text and takes a plain YES as the approval. You book the day.",
  customer_updates: "One note when a thread went quiet and no price had gone out.",
  commission_accrual: "Works out what each technician earned. Only ever adds.",
  // This automation IS the New Mexico tax, so naming the state is the
  // switch's meaning rather than a rule aimed at a shop in another one.
  grt_tax: "Adds the New Mexico gross receipts tax to every quote, as a line you can still edit.", // jurisdiction-ok

  job_alerts: "Technician at T-60, customer at T-45, so the two never land together.",
  text_desk: "Answers staff texts and asks you for approvals.",
  text_desk_customer: "The standard customer texts a technician can trigger by text.",
  team_report: "What the team did, to you at 9am and 9pm.",
  front_desk_feed: "The bell rings for a landline that cannot be texted, or one of ours waiting.",

  claim_campaign: "MechanicRank claim texts to shops. Nothing to do with this shop's customers.",
  ppi_refer: "Inspections too far away to drive to, offered to the network. Sends the customer's details out.",
  dev_builder: "Takes over the build in the cloud when the desktop goes quiet.",
};

const GROUPS: Array<{ title: string; blurb: string; keys: string[] }> = [
  {
    title: "The front door",
    blurb: "Strangers, and whether they get answered.",
    keys: ["lead_textback", "textback_nudge", "autofix_line", "intake_agent", "callrail_intake",
      "callrail_texts", "email_lead", "ghl_forms", "car_catcher"],
  },
  {
    title: "Quotes and money",
    blurb: "Pricing the work and chasing the yes.",
    keys: ["quote_drafter", "quote_chase", "stale_quotes", "ppi_quote_send", "ppi_quote_drip", "ppi_sms_brand", "visit_quote_send",
      "customer_updates", "commission_accrual", "grt_tax"],
  },
  {
    title: "The crew",
    blurb: "People who can ask a human when something stops.",
    keys: ["job_alerts", "text_desk", "text_desk_customer", "team_report", "front_desk_feed"],
  },
  {
    title: "Elsewhere",
    blurb: "Not this shop's customers.",
    keys: ["claim_campaign", "ppi_refer", "dev_builder"],
  },
];

/**
 * REHEARSING IS NOT THE SAME AS ON, AND THE SCREEN HAS TO SAY SO.
 *
 * settings.forward_to is the rehearsal switch: while it holds a number,
 * every reply goes there and the person who texted in hears nothing. The
 * row read a plain "ON" either way, which is the most dangerous thing a
 * state indicator can do -- it said strangers were being answered when
 * they were not, and it would have said the same if they were.
 *
 * Read only. Starting a rehearsal needs a phone number typed in and the
 * tool does that safely; a half-built rehearsal switch on a screen is
 * worse than none, because somebody would press it believing it rehearsed.
 */
const rehearsingTo = (a: Row): string => {
  const raw = String((a.settings ?? {})["forward_to"] ?? "").replace(/\D/g, "");
  return raw.length === 10 ? raw : "";
};

const prettyPhone = (p: string): string =>
  `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;

const hours = (a: Row): string =>
  a.window_start === null || a.window_end === null ? "any hour"
    : a.window_start === 0 && a.window_end === 24 ? "any hour"
      : `${a.window_start}:00 to ${a.window_end}:00`;

export async function automationsPage(role: Role = "viewer"): Promise<void> {
  if (!BOSS.includes(role)) {
    mount(`<div class="page"><div class="card"><div class="empty">
      Automations are an owner's screen.</div></div></div>`);
    return;
  }

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Automations</h1>
      <p>Everything that runs without you. Switching one off is safe. Nothing is lost,
         it stops until you turn it back on.</p></header>
    <div id="autos"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  const host = document.getElementById("autos");
  if (!host) return;

  const draw = async (): Promise<void> => {
    const sb = await db();
    const { data, error } = await sb.from("automations")
      .select("key,enabled,daily_cap,window_start,window_end,floor,note,settings");
    if (error) {
      host.innerHTML = `<div class="card"><div class="empty bad">${esc(error.message)}</div></div>`;
      return;
    }
    const rows = (data ?? []) as Row[];
    const by = new Map(rows.map((r) => [r.key, r]));

    /**
     * One read for the whole screen. The Text Desk card asks per
     * automation, which is fine for two and would be twenty round trips
     * here -- on a phone in a bay that is the difference between a screen
     * that opens and one people stop opening.
     */
    const sent: Record<string, number> = {};
    const { data: today } = await sb.from("comm_events")
      .select("raw").eq("direction", "outbound")
      .gte("occurred_at", shopDayStartIso()).limit(2000);
    for (const e of (today ?? []) as Array<{ raw: Record<string, unknown> | null }>) {
      const k = String((e.raw ?? {})["automation"] ?? "");
      if (k) sent[k] = (sent[k] ?? 0) + 1;
    }

    const on = rows.filter((r) => r.enabled).length;
    const off = rows.length - on;

  const line = (a: Row): string => {
    const reh = rehearsingTo(a);
    return `
      <div class="row${a.enabled ? "" : " isoff"}">
        <div class="row-main">
          <div class="row-title">
            <span class="num">${esc(a.key.replace(/_/g, " "))}</span>
            <span class="onoff ${reh ? "is-reh" : a.enabled ? "is-on" : "is-off"}">${reh ? "REHEARSAL" : a.enabled ? "ON" : "OFF"}</span>
          </div>
          <div class="row-sub">${reh ? `<b class="rehsay">Rehearsal &mdash; every reply goes to ${esc(prettyPhone(reh))}, whoever texted in hears nothing.</b>` : esc(SAYS[a.key] ?? a.note ?? "")}</div>
          <div class="row-note">${sent[a.key] ?? 0} sent today${
            a.daily_cap ? ` of ${a.daily_cap}` : ""} &middot; ${esc(hours(a))}${
            a.enabled && a.floor ? ` &middot; on since ${esc(since(a.floor))}` : ""}</div>
        </div>
        <div class="row-side">
          <button class="btn sm ${a.enabled ? "danger" : "primary"}"
                  data-key="${esc(a.key)}" data-to="${a.enabled ? "off" : "on"}"
                  aria-label="Switch ${esc(a.key.replace(/_/g, " "))} ${a.enabled ? "off" : "on"}">
            ${a.enabled ? "Switch off" : "Switch on"}</button>
          ${reh ? `<button class="btn sm primary" data-golive="${esc(a.key)}"
                  aria-label="Take ${esc(a.key.replace(/_/g, " "))} live">Go live</button>` : ""}
        </div>
      </div>`;
  };

    const known = new Set(GROUPS.flatMap((g) => g.keys));
    const rest = rows.filter((r) => !known.has(r.key));

    host.innerHTML = `
      <div id="auto-msg"></div>
      <div class="card"><div class="pad autosum">
        <span class="onoff is-on">${on} ON</span>
        ${off ? `<span class="onoff is-off">${off} OFF</span>` : ""}
        <span class="fine">Turning one on starts it from now, so nothing older is ever chased.</span>
      </div></div>
      ${GROUPS.map((g) => {
        const found = g.keys.map((k) => by.get(k)).filter(Boolean) as Row[];
        if (!found.length) return "";
        return `<div class="card">
          <div class="card-head">${esc(g.title)}</div>
          <div class="pad"><p class="fine">${esc(g.blurb)}</p></div>
          ${found.map(line).join("")}
        </div>`;
      }).join("")}
      ${rest.length
        ? `<div class="card"><div class="card-head">Everything else</div>${
          rest.map(line).join("")}</div>`
        : ""}`;

    /**
     * ENDING A REHEARSAL, WHICH IS THE ONLY HALF OF IT THIS SCREEN DOES.
     *
     * Nulls forward_to and re-floors, leaving enabled alone. The floor
     * matters: without it, going live could answer everything that arrived
     * during the rehearsal, all at once, to people who have already been
     * dealt with.
     *
     * The same thing `tools/autofix-switch.mjs live` does, so a rehearsal
     * that began at a terminal can end here.
     */
    host.querySelectorAll<HTMLButtonElement>("button[data-golive]").forEach((b) =>
      b.addEventListener("click", async () => {
        const key = b.dataset["golive"] ?? "";
        if (!confirm("Real strangers get answered from this moment. Go live?")) return;
        const was = b.textContent ?? "";
        b.disabled = true; b.textContent = "Going live…";
        const row = rows.find((r) => r.key === key);
        const settings = { ...(row?.settings ?? {}), forward_to: null };
        const { error: e3 } = await sb.from("automations")
          .update({ settings, floor: new Date().toISOString() }).eq("key", key);
        if (e3) {
          b.disabled = false; b.textContent = was;
          const m = document.getElementById("auto-msg");
          if (m) m.innerHTML = `<p class="msg bad">That did not go live: ${esc(e3.message)}</p>`;
          return;
        }
        await draw();
      }));

    host.querySelectorAll<HTMLButtonElement>("button[data-key]").forEach((b) =>
      b.addEventListener("click", async () => {
        const key = b.dataset["key"] ?? "";
        const to = b.dataset["to"] === "on";
        const name = key.replace(/_/g, " ");
        // Only switching OFF asks. Turning one back on cannot hurt anybody
        // -- the floor sees to that -- and a confirm on a safe action
        // teaches people to tap through the one that is not.
        if (!to && !confirm(
          `Switch ${name} off? It stops until you turn it back on. Nothing is lost.`)) return;
        const was = b.textContent ?? "";
        b.disabled = true;
        b.textContent = to ? "Switching on..." : "Switching off...";
        const patch = to
          ? { enabled: true, floor: new Date().toISOString() }
          : { enabled: false };
        const { error: e2 } = await sb.from("automations").update(patch).eq("key", key);
        if (e2) {
          b.disabled = false;
          b.textContent = was;
          const m = document.getElementById("auto-msg");
          if (m) m.innerHTML = `<p class="msg bad">That did not switch: ${esc(e2.message)}</p>`;
          return;
        }
        await draw();
      }));
  };

  try {
    await draw();
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}
